import {
  ActionBar,
  ChakraFlex,
  FAIconName,
  Text,
  useToastMessage,
} from '@equip.health/ui';
import { Section } from '~/components/maud/inquiries/InquiryDetail/sections';
import {
  GetDischargePlanResponse,
  useGetDischargePlanTasksQuery,
  useUpdateDischargePlanStatusMutation,
} from '~/store/services/clinical-data/dischargePlan';
import { useState, useCallback, useMemo } from 'react';
import SignDischargePlanModal from './modals/SignDischargePlanModal';
import ReviseDischargePlanModal from './modals/ReviseDischargePlanModal';
import DischargePlanHeaderAction, {
  DischargeModalType,
} from './DischargePlanHeaderSectionAction';
import {
  getPreppingTasks,
  getRequiredDischargeDetailsFieldErrors,
} from './util/dischargePlanUtils';
import { useDischargePlanViewContext } from './DischargePlanViewContext';
import {
  useGetProviderAvatarByIdQuery,
  useGetProviderByIdQuery,
} from '~/store/services/providers';
import { DischargePlanStatuses } from './common/dischargePlanConstants';
import ProviderAvatarWithDate from './common/ProviderAvatarWithDate';
import ReviewIncompleteTaskModal from './modals/ReviewIncompleteTaskModal';
import { TaskStatusLabel } from '~/lib/util/task.util';
import { useGetSecondaryReasonsQuery } from '~/store/services/clinical-data/refDischargePlan';
interface DischargePlanHeaderSectionProps {
  dischargePlanData?: GetDischargePlanResponse;
  isDischarged: boolean;
  isStartButtonDisabled: boolean;
}

const DischargePlanHeaderSection = ({
  dischargePlanData,
  isDischarged,
  isStartButtonDisabled,
}: DischargePlanHeaderSectionProps) => {
  const isSigned = useMemo(() => {
    return (
      dischargePlanData?.status?.code === DischargePlanStatuses.SIGNED.code
    );
  }, [dischargePlanData?.status]);

  const signedByUserId = dischargePlanData?.signedByUserId || null;

  const { data: signedByUser, isLoading: isGetProviderLoading } =
    useGetProviderByIdQuery(
      {
        provideAuthDetails: false,
        providerExternalId: signedByUserId,
      },
      { skip: !signedByUserId || !isSigned },
    );
  const { data: avatarData, isLoading: isGetProviderAvatarLoading } =
    useGetProviderAvatarByIdQuery(
      {
        providerExternalId: signedByUser?.userId,
      },
      {
        skip: !signedByUser?.userId || !isSigned,
      },
    );

  const [
    updateDischargePlanStatus,
    { isLoading: isUpdateDischargePlanStatusLoading },
  ] = useUpdateDischargePlanStatusMutation();

  const [showSignModal, setShowSignModal] = useState<boolean>(false);
  const [showReviseModal, setShowReviseModal] = useState<boolean>(false);
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [justification, setJustification] = useState<string>('');
  const {
    data: dischargePlanTasks,
    isLoading,
    isError,
  } = useGetDischargePlanTasksQuery(
    {
      dischargePlanId: dischargePlanData?.id,
      pageSize: 25,
      pageNumber: 1,
    },
    {
      skip: !dischargePlanData?.id || isDischarged,
    },
  );
  const { data: secondaryReasonsData, isLoading: isSecondaryReasonsLoading } =
    useGetSecondaryReasonsQuery();
  const { createToast } = useToastMessage();

  const {
    errors: { dischargeDetails: dischargeDetailsErrors },
    setDischargeDetailsErrors,
  } = useDischargePlanViewContext();
  const hasDischargeDetailsErrors = Object.values(dischargeDetailsErrors).some(
    (isError) => isError,
  );

  const preppingTasks = getPreppingTasks(dischargePlanTasks?.data) ?? [];
  const pendingPreppingTasks = preppingTasks?.filter(
    (task) =>
      task?.statusCode === TaskStatusLabel.TO_DO ||
      task?.statusCode === TaskStatusLabel.IN_PROGRESS,
  );

  const handleSuccess = useCallback(() => {
    createToast({
      icon: FAIconName.checkRegular,
      id: 'start-prepping-discharge-plan-success',
      text: 'Started prepping discharge plan',
      variant: 'success',
    });
  }, [createToast]);

  const handleFailure = useCallback(() => {
    createToast({
      icon: FAIconName.exclamationTriangleRegular,
      id: 'start-prepping-discharge-plan-error',
      text: "We're sorry, we were unable to update the discharge plan status. Please try again later",
      variant: 'critical',
    });
  }, [createToast]);

  const hasPendingPreppingTasks = pendingPreppingTasks?.length > 0;
  const handleShowModal = useCallback(
    (modalType: DischargeModalType) => {
      if (modalType === 'start') {
        if (!dischargePlanData?.id || !dischargePlanData?.status?.code) {
          return;
        }

        updateDischargePlanStatus({
          currentStatusCode: dischargePlanData?.status?.code,
          dischargePlanId: dischargePlanData?.id,
          statusCode: DischargePlanStatuses.PREPPING.code,
        })
          .unwrap()
          .then(() => handleSuccess())
          .catch(() => handleFailure());
      }

      if (modalType === 'revise') {
        setShowReviseModal(true);
        return;
      }

      if (modalType === 'sign') {
        const fieldErrors = getRequiredDischargeDetailsFieldErrors(
          dischargePlanData,
          secondaryReasonsData,
        );

        setDischargeDetailsErrors(fieldErrors);

        const hasFieldErrors = Object.values(fieldErrors).some(Boolean);
        if (hasFieldErrors) {
          return;
        }
        if (isError) {
          throw new Error('Failed to load discharge tasks');
        }

        if (hasPendingPreppingTasks) {
          setShowReviewModal(true);
          return;
        }
        setShowSignModal(true);
      }
    },
    [
      dischargePlanData,
      hasPendingPreppingTasks,
      updateDischargePlanStatus,
      handleSuccess,
      handleFailure,
      secondaryReasonsData,
      setDischargeDetailsErrors,
      isError,
    ],
  );
  if (isLoading) {
    return null;
  }

  return (
    <Section id="discharge-plan-header">
      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        marginTop="18px"
      >
        <ChakraFlex alignItems="center" gap="20px" justifyContent="inherit">
          <Text variant="h4">Discharge plan</Text>
          <ProviderAvatarWithDate
            avatarUrl={avatarData?.url}
            firstName={signedByUser?.firstName}
            isHide={!isSigned}
            isLoading={isGetProviderLoading || isGetProviderAvatarLoading}
            lastName={signedByUser?.lastName}
            providerExternalId={signedByUser?.userId}
            signedAt={dischargePlanData?.signedAt}
          />
        </ChakraFlex>
        <DischargePlanHeaderAction
          isDischarged={isDischarged}
          isStartDischargePlanLoading={isUpdateDischargePlanStatusLoading}
          isReadyToDischargeLoading={isSecondaryReasonsLoading}
          isStartButtonDisabled={isStartButtonDisabled}
          showModal={handleShowModal}
          status={dischargePlanData?.status}
        />
      </ChakraFlex>
      {hasDischargeDetailsErrors && (
        <ActionBar
          barIcon={FAIconName.exclamationTriangleRegular}
          id="discharge-details-required-fields-error"
          text="Please complete all required fields before marking the patient ready to discharge"
          variant="critical"
        />
      )}
      {showReviewModal && (
        <ReviewIncompleteTaskModal
          isOpen={showReviewModal}
          justification={justification}
          onClose={() => {
            setShowReviewModal(false);
          }}
          onProceed={() => setShowSignModal(true)}
          setJustification={setJustification}
          tasks={pendingPreppingTasks}
        />
      )}
      <SignDischargePlanModal
        dischargePlanId={dischargePlanData?.id}
        isOpen={showSignModal}
        justification={justification}
        onClose={() => {
          setShowSignModal(false);
          setJustification('');
        }}
      />
      <ReviseDischargePlanModal
        dischargePlanId={dischargePlanData?.id}
        isOpen={showReviseModal}
        onClose={() => setShowReviseModal(false)}
      />
    </Section>
  );
};

export default DischargePlanHeaderSection;
